/**
 * Atlas — motion vocabulary.
 *
 * The feel is ARRIVAL. The panorama settles you somewhere; the guess pin drops
 * with a small weight; the reveal does NOT sweep — the thread from your guess
 * to the true place FADES IN like ink soaking into paper, and the place card
 * turns up from below like a page in the atlas (a 2D material exchange, never a
 * rotateY flip — POLISH-GATE item 3).
 *
 * These springs are tuned for THIS world and are deliberately calmer and
 * heavier than Five's letterpress snap — a globe turning, a card settling, a
 * pin finding weight. `juice/easing.ts` + `juice/timings.ts` are starter
 * tokens; we invent our own here.
 */

/** The whole page settling in — a calm, slightly heavy arrival. */
export const pageSpring = {
  type: 'spring' as const,
  stiffness: 170,
  damping: 26,
  mass: 1.2,
};

/** A scope card / hub element drifting up into place. */
export const cardSpring = {
  type: 'spring' as const,
  stiffness: 220,
  damping: 24,
  mass: 0.95,
};

/** Hover lift on a card — gentle, never a jump. */
export const hoverSpring = {
  type: 'spring' as const,
  stiffness: 320,
  damping: 26,
};

/** The guess pin dropping onto the map — a little weight, a small settle. */
export const pinDropSpring = {
  type: 'spring' as const,
  stiffness: 460,
  damping: 17,
  mass: 0.7,
};

/**
 * The place card rising from below at the reveal — the page turning up. A soft
 * overshoot so it "lands" rather than slides.
 */
export const placeCardSpring = {
  type: 'spring' as const,
  stiffness: 200,
  damping: 22,
  mass: 1.0,
};

/** A star stamping in at the reveal — a firm, brief press. */
export const starSpring = {
  type: 'spring' as const,
  stiffness: 360,
  damping: 14,
  mass: 0.6,
};

/** The compass glow at the arrival celebration — broad and slow. */
export const arrivalSpring = {
  type: 'spring' as const,
  stiffness: 120,
  damping: 20,
  mass: 1.1,
};

// ---- Timings (ms) ---------------------------------------------------------

/** How long the reveal thread takes to fade fully in. */
export const THREAD_FADE_MS = 900;
/** Delay before the place card rises, so the thread + distance read first. */
export const PLACE_CARD_DELAY_MS = 700;
/** Stagger between stars stamping in. */
export const STAR_STAGGER_MS = 110;
/** Hub card entry stagger. */
export const HUB_STAGGER_S = 0.07;
/** How long the arrival glow lingers. */
export const ARRIVAL_GLOW_MS = 1500;
